import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useInflight } from '../lib/loading';

/**
 * 화면 가운데 **로딩 카드** — 조회 요청이 나가 있는 동안 뜬다(2026-09-17 사용자 요청).
 *
 * 예전 표시는 표 안쪽 회색 `불러오는 중…` 한 줄이라 **빈 표를 "자료가 없다"로 읽었다.**
 * 요청 수는 `lib/api.ts` 의 `call()` 이 세므로 전 화면에 한 번에 걸린다.
 *
 * 규칙
 * - 🔴 **입력을 막지 않는다**(`pointer-events-none`). 모달 모양이지만 조회는 검색어를 치는 도중에도
 *   나간다 — 막으면 글자를 치다 말고 멈춘다. 바탕은 아주 옅게만 깐다(뒤 표는 그대로 읽힌다).
 * - **늦게 뜨고 잠깐 남는다.** 웜 상태 조회는 0.2~0.4초라 곧장 띄우면 번쩍이기만 한다 →
 *   `SHOW_AFTER_MS` 넘게 걸릴 때만 띄우고, 한 번 뜨면 `MIN_VISIBLE_MS` 는 남겨 깜빡임을 없앤다.
 * - **오래 걸리면 이유를 적는다.** 3초가 넘으면 대개 서버 컨테이너가 식어 있던 것이다(`lib/warm.ts`) —
 *   말없이 돌기만 하면 멈춘 것처럼 보인다.
 */
/**
 * 🔴 2026-09-29 속도 개선(way 「데이터 불러오기 창이 너무 자주 뜬다 · 마우스도 느려진다」):
 *    350ms 에 카드를 띄우니 웜 상태(0.2~1초)에서도 화면을 옮길 때마다 카드가 번쩍였다.
 *    → 짧은 조회는 **맨 위 얇은 막대**만(BAR_AFTER_MS), 카드는 정말 오래 걸릴 때만(SHOW_AFTER_MS).
 *    카드 뒤 흐림(backdrop-blur)·화면 전체 옅은 막도 뺐다 — 큰 표 위에서 매 프레임 다시 그려 느린 PC 에서 마우스가 끌린다.
 * 🔴 2026-10-28 way 「이 현상이 너무 잦아서 업무에 불편할 정도」(개선요청 상세 창 위에 카드):
 *    1.2초 기준이라 **내용이 이미 보이는 화면의 다시 불러오기**(코멘트·상태 바꾼 뒤 등)에도 카드가 떴다.
 *    빈 표는 DataTable 이 「불러오는 중…」을 직접 쓰므로(09-17 의 「자료 없음」 오해는 거기서 막힌다)
 *    카드는 **정말 오래 걸릴 때(4초 — 대개 서버가 식어 있던 것)만**, 그 전엔 맨 위 막대만(조금 굵게).
 */
const BAR_AFTER_MS = 300;
const SHOW_AFTER_MS = 4000;
const MIN_VISIBLE_MS = 400;
const SLOW_MS = 8000;

export default function LoadingOverlay() {
  const inflight = useInflight();
  const busy = inflight > 0;
  const [visible, setVisible] = useState(false);
  const [shownAt, setShownAt] = useState(0);
  const [slow, setSlow] = useState(false);
  const [bar, setBar] = useState(false);

  // 얇은 막대: 요청이 BAR_AFTER_MS 넘게 이어지면 켜고, 끝나면 바로 끈다.
  useEffect(() => {
    if (!busy) { setBar(false); return; }
    const t = setTimeout(() => setBar(true), BAR_AFTER_MS);
    return () => clearTimeout(t);
  }, [busy]);

  // 켜기: 요청이 SHOW_AFTER_MS 넘게 이어질 때만.
  useEffect(() => {
    if (!busy || visible) return;
    const t = setTimeout(() => {
      setVisible(true);
      setShownAt(Date.now());
    }, SHOW_AFTER_MS);
    return () => clearTimeout(t);
  }, [busy, visible]);

  // 끄기: 요청이 다 끝났고 최소 노출 시간을 채웠을 때.
  useEffect(() => {
    if (busy || !visible) return;
    const left = Math.max(0, MIN_VISIBLE_MS - (Date.now() - shownAt));
    const t = setTimeout(() => {
      setVisible(false);
      setSlow(false);
    }, left);
    return () => clearTimeout(t);
  }, [busy, visible, shownAt]);

  // 오래 걸리면 안내 문구를 붙인다.
  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => setSlow(true), Math.max(0, SLOW_MS - SHOW_AFTER_MS));
    return () => clearTimeout(t);
  }, [visible]);

  if (!visible) {
    if (!bar) return null;
    return createPortal(
      <div className="pointer-events-none fixed inset-x-0 top-0 z-[60] h-1 overflow-hidden bg-brand-100" role="status" aria-label="불러오는 중">
        <div className="h-full w-1/3 animate-loadbar bg-brand-600" />
      </div>,
      document.body,
    );
  }

  // Modal 과 같은 이유로 body 에 띄운다(조상의 transform 에 fixed 가 갇히지 않게).
  return createPortal(
    <div
      className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center animate-fade-in"
      role="status"
      aria-live="polite"
    >
      <div className="flex min-w-[15rem] items-center gap-4 rounded-2xl border border-line/80 bg-surface px-6 py-5 shadow-xl shadow-ink/10">
        <span className="relative inline-flex h-9 w-9 shrink-0">
          <span className="absolute inset-0 rounded-full border-[3px] border-brand-100" />
          <span className="absolute inset-0 animate-spin rounded-full border-[3px] border-transparent border-t-brand-600" />
        </span>
        <div>
          <div className="text-sm font-semibold text-ink">데이터를 불러오는 중입니다</div>
          <div className="mt-0.5 break-keep text-xs text-ink-soft">
            {slow ? '조금 더 걸리고 있습니다. 잠시만 기다려 주세요.' : '잠시만 기다려 주세요.'}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
